import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, CheckList, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAEVATReverseChargeMetalScrapGuide = () => {
  const guide = {
    ...getGuideBySlug('uae-vat-reverse-charge-metal-scrap-vatp047'),
    relatedService: { label: 'VAT Services', path: '/vat-services-uae' }
  };

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'what-is-rcm', label: 'What is the Reverse Charge Mechanism?' },
    { id: 'qualifying-goods', label: 'Which Goods are Treated as Metal Scrap?' },
    { id: 'qualifying-transactions', label: 'Which Transactions Qualify?' },
    { id: 'resale-processing', label: 'Resale and Processing Defined' },
    { id: 'recipient-duties', label: "Recipient's Compliance Responsibilities" },
    { id: 'supplier-duties', label: "Supplier's Compliance Responsibilities" },
    { id: 'vat-return', label: 'VAT Return Reporting' },
    { id: 'exclusions', label: 'When the Special Treatment Does Not Apply' },
    { id: 'missing-declarations', label: 'What Happens if Declarations are Missing' },
    { id: 'example', label: 'Practical Example' },
    { id: 'checklist', label: 'Compliance Checklist' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' }
  ];

  return (
    <ArticleShell
      guide={guide}
      toc={toc}
      testId="ka-article-vatp047"
      ctaService="vat"
      heroImage={{
        src: 'https://customer-assets-jai6qajn.emergentagent.net/job_606545ce-2ce7-41b6-8e7f-1b01a327e549/artifacts/b0d30137c5921d55_Metal%20Scrap.png',
        alt: 'UAE VAT Reverse Charge on Metal Scrap — FTA Public Clarification VATP047 explained by WPAA',
        width: 1920,
        height: 1280
      }}
    >
      <Callout variant="info" title="At a glance">
        The underlying requirements, introduced by <strong>Cabinet Decision No. 153 of 2025</strong>,
        apply to qualifying supplies of metal scrap made on or after <strong>14 January 2026</strong>.
        VATP047 does not change the effective date — it explains how to apply the rules correctly.
      </Callout>

      <H2 id="overview">Overview</H2>
      <P>
        The UAE Federal Tax Authority (FTA) issued VAT Public Clarification <strong>VATP047</strong>{' '}
        in October 2026 to explain how the Reverse Charge Mechanism applies to qualifying metal scrap
        transactions between VAT-registered businesses in the UAE.
      </P>
      <P>
        Businesses dealing in metal scrap should review their customer declarations, Tax Registration
        Number (TRN) verification procedures, tax invoices and VAT return reporting. A failure to
        satisfy the required conditions can change the VAT treatment of the transaction and may prevent
        the recipient from recovering the related input VAT.
      </P>

      <H2 id="what-is-rcm">What is the Reverse Charge Mechanism?</H2>
      <P>
        Under the normal VAT rules, a supplier charges VAT on a taxable supply, collects it from the
        customer and reports the output tax in its VAT return.
      </P>
      <P>
        Under the Reverse Charge Mechanism, the responsibility for accounting for VAT moves from the
        supplier to the recipient. For a qualifying metal scrap transaction:
      </P>
      <BulletList items={[
        'The supplier does not charge VAT on the supply.',
        'The recipient accounts for the VAT under the Reverse Charge Mechanism.',
        'The recipient may recover the VAT as recoverable tax, subject to the normal input-tax recovery rules and the specific requirements explained in VATP047.'
      ]} />
      <P>
        The Reverse Charge Mechanism applies <strong>only</strong> when all prescribed eligibility and
        documentation conditions are satisfied.
      </P>

      <H2 id="qualifying-goods">Which Goods are Treated as Metal Scrap?</H2>
      <P>
        VATP047 defines <strong>metal scrap</strong> as ferrous or non-ferrous metal waste that has
        commercial value and can be used following processing.
      </P>
      <P>
        This may include goods that mainly consist of qualifying metal material. For example, a used
        lead-acid battery that has reached the end of its useful life may be treated as metal scrap
        where lead forms the majority of its composition and is the main component to be extracted
        through processing.
      </P>
      <P>
        The special treatment <strong>does not extend</strong> to every type of scrap. The following
        materials remain subject to the normal VAT rules:
      </P>
      <BulletList items={[
        'Paper',
        'Plastic',
        'Glass',
        'Textiles',
        'Organic waste',
        'Rubber'
      ]} />
      <Callout variant="warning" title="Mixed or composite scrap">
        Businesses selling mixed or composite scrap should assess the principal material and the
        intended processing rather than assuming that every waste product falls within the metal
        scrap rules.
      </Callout>

      <H2 id="qualifying-transactions">Which Transactions Qualify?</H2>
      <P>The Reverse Charge Mechanism applies when all of the following conditions are met:</P>
      <CheckList items={[
        'The supplier provides qualifying metal scrap to the recipient.',
        'Both the supplier and the recipient are registered for UAE VAT.',
        'The recipient purchases the metal scrap with the intention of reselling it or using it in processing.',
        'The recipient provides the required written declarations to the supplier before the date of supply.',
        "The supplier verifies the recipient's VAT registration through the FTA's TRN verification facility.",
        'The supplier retains the required declarations and issues a compliant tax invoice stating that the Reverse Charge Mechanism applies.'
      ]} />
      <P>
        If <strong>any</strong> required condition is not satisfied, the normal VAT treatment may apply.
      </P>

      <H2 id="resale-processing">What Do &ldquo;Resale&rdquo; and &ldquo;Processing&rdquo; Mean?</H2>

      <H3>Resale</H3>
      <P>
        Resale means selling the metal scrap as part of the recipient&apos;s business of trading in
        that scrap. The resale may take place at wholesale or retail level.
      </P>
      <P>
        Metal scrap purchased for another internal business purpose is <strong>not</strong> treated as
        having been purchased for resale. For example, scrap metal acquired for repairing storage
        racks, fences or structural supports at the recipient&apos;s premises would not satisfy the
        resale condition.
      </P>

      <H3>Processing</H3>
      <P>
        Processing means converting metal scrap into material that can be used to manufacture new
        products. This may take place through repairing, recycling or another conversion method.
      </P>
      <P>
        For example, metal scrap acquired for conversion into construction material, building products
        or new vehicle parts may satisfy the processing condition.
      </P>

      <H2 id="recipient-duties">Recipient&apos;s Compliance Responsibilities</H2>
      <P>
        Before the date of supply, the recipient must provide the supplier with written confirmation
        that:
      </P>
      <BulletList items={[
        'The recipient is registered for VAT, including its TRN.',
        'The metal scrap is being purchased for resale or for use in processing.'
      ]} />
      <P>The two confirmations may be combined into one declaration.</P>
      <Callout variant="warning" title="Timing matters">
        The declarations must be provided <strong>before the date of supply</strong>, not afterwards
        as a corrective administrative step.
      </Callout>
      <P>
        The recipient must then account for VAT on the qualifying transaction and comply with the
        applicable VAT return reporting requirements.
      </P>

      <H2 id="supplier-duties">Supplier&apos;s Compliance Responsibilities</H2>
      <P>Before applying the Reverse Charge Mechanism, the supplier should:</P>
      <CheckList items={[
        "Receive and retain the recipient's written declarations.",
        "Verify the recipient's VAT registration using the FTA's TRN verification facility.",
        'Retain evidence of the verification as part of the transaction records.',
        'Clearly state on the tax invoice that the supply of metal scrap is subject to the Reverse Charge Mechanism.'
      ]} />
      <P>
        The supplier should <strong>not</strong> rely only on a TRN printed on a purchase order or
        communicated informally. The FTA clarification specifically requires the supplier to verify
        the recipient&apos;s registration.
      </P>

      <H2 id="vat-return">How Should the Transaction Be Reported in the VAT Return?</H2>
      <P>Where all eligibility requirements are satisfied, the reporting treatment is as follows:</P>
      <div className="my-8 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-teal-50 text-teal-900">
            <tr>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">Party</th>
              <th className="text-left font-semibold uppercase tracking-wider text-[11px] px-4 py-3 border-b border-teal-100">VAT-Return Treatment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-white align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Supplier</td>
              <td className="px-4 py-3 text-gray-700">
                Does not account for VAT and does not report the qualifying metal scrap supply or
                related VAT in its VAT return.
              </td>
            </tr>
            <tr className="bg-gray-50/60 align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Recipient</td>
              <td className="px-4 py-3 text-gray-700">
                Accounts for the supply in <strong>Box 3</strong> of the VAT return.
              </td>
            </tr>
            <tr className="bg-white align-top">
              <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">Recipient&apos;s recovery</td>
              <td className="px-4 py-3 text-gray-700">
                May recover the recoverable tax in <strong>Box 10</strong>, subject to the applicable
                recovery conditions. <strong>Box 9 should not be used</strong> for this transaction.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Callout variant="info" title="System configuration">
        Businesses should configure their accounting systems and VAT tax codes carefully so that
        qualifying metal scrap transactions flow into the correct VAT-return boxes.
      </Callout>

      <H2 id="exclusions">When Does the Special Treatment Not Apply?</H2>
      <P>The metal scrap Reverse Charge Mechanism does not apply in several situations, including where:</P>
      <BulletList items={[
        'The goods do not meet the definition of metal scrap.',
        'Either party is not registered for VAT.',
        'The recipient does not intend to resell or process the metal scrap.',
        'The recipient purchases the scrap for internal use other than processing.',
        'The required declarations are not provided before the date of supply.',
        'The supplier makes a qualifying direct or indirect export of metal scrap.'
      ]} />
      <P>
        Where a direct or indirect export meets the conditions for zero-rating, the normal VAT rules
        apply and the supplier reports the transaction as a zero-rated supply.
      </P>

      <H2 id="missing-declarations">What Happens if the Declarations are Missing?</H2>
      <P>
        If the recipient does not provide the required declarations before the date of supply, the
        Reverse Charge Mechanism conditions are not met.
      </P>
      <P>In that situation:</P>
      <BulletList items={[
        'The supplier must apply VAT at the standard rate.',
        'The supplier reports the supply in Box 1 of its VAT return.',
        'According to VATP047, the recipient is not eligible to recover the input VAT incurred on that purchase.'
      ]} />
      <Callout variant="danger" title="Documentation is not optional">
        This makes the declarations more than a routine administrative formality. Missing or late
        documentation can create a direct tax cost for the purchaser and a compliance exposure for
        both parties.
      </Callout>

      <H2 id="example">Practical Example</H2>
      <P>
        <strong>Emirates Metal Trading LLC</strong>, a VAT-registered business, purchases copper
        scrap from another UAE VAT-registered supplier for resale to industrial processors.
      </P>
      <P>
        Before the date of supply, Emirates Metal Trading LLC gives the supplier a written
        declaration confirming its TRN and stating that the copper scrap is being purchased for
        resale. The supplier verifies the TRN through the FTA facility and issues a tax invoice
        stating that the Reverse Charge Mechanism applies.
      </P>
      <P>In this case:</P>
      <BulletList items={[
        'The supplier does not charge VAT.',
        'The recipient accounts for VAT in Box 3.',
        'The recipient may recover the eligible amount in Box 10.'
      ]} />
      <P>
        If the declaration had not been provided before the date of supply, the supplier would
        instead charge VAT at the standard rate and report the transaction in Box 1. The recipient
        would not be entitled to recover that VAT under the treatment explained in VATP047.
      </P>

      <H2 id="checklist">Compliance Checklist for Metal Scrap Businesses</H2>

      <H3>For Suppliers</H3>
      <CheckList items={[
        'Confirm that the goods qualify as metal scrap.',
        "Establish the recipient's intended use of the goods.",
        "Obtain the recipient's written declarations before the date of supply.",
        "Verify the recipient's TRN through the FTA facility.",
        'Retain the declarations and verification evidence.',
        'Include the Reverse Charge Mechanism statement on the tax invoice.',
        'Apply the correct accounting and VAT-return tax code.'
      ]} />

      <H3>For Recipients</H3>
      <CheckList items={[
        'Confirm that the purchase is genuinely for resale or processing.',
        'Provide the required declarations before the date of supply.',
        'Give the supplier accurate VAT-registration information.',
        'Account for the transaction in Box 3.',
        'Claim eligible recoverable tax in Box 10, not Box 9.',
        'Retain purchase, declaration, invoice and processing or resale records.'
      ]} />

      <Callout variant="info" title="Key takeaway">
        VATP047 gives businesses clearer guidance on the scope and operation of the Reverse Charge
        Mechanism for metal scrap. However, the treatment depends on the nature of the goods, the
        recipient&apos;s intended use, both parties&apos; VAT-registration status and strict
        completion of the required documentation <strong>before the date of supply</strong>. Metal
        scrap suppliers and purchasers should review their transaction workflows now &mdash;
        particularly their declarations, TRN checks, invoice wording and VAT return mappings.
      </Callout>

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>White Paper Accounts Auditing can assist businesses with:</P>
      <CheckList items={[
        'Reviewing metal scrap transactions and VAT treatment',
        'Preparing recipient declaration templates',
        'Checking tax-invoice wording',
        'Reviewing accounting-system VAT codes',
        'Reconciling VAT return Boxes 1, 3, 9 and 10',
        'Conducting VAT compliance reviews before filing'
      ]} />
      <Callout variant="info" title="Tailored advice">
        For advice tailored to your business, contact the WPAA Tax &amp; Advisory Team.
      </Callout>

      <H2>Official Reference</H2>
      <BulletList items={[
        'UAE Federal Tax Authority — VAT Public Clarification VATP047: Application of the Reverse Charge Mechanism on Metal Scrap Trading among UAE VAT Registrants (October 2026).',
        'Cabinet Decision No. 153 of 2025.'
      ]} />
      <P className="text-xs text-gray-500 italic mt-6">Last reviewed: October 2026.</P>

      <Callout variant="warning" title="Disclaimer">
        This article is intended for general informational purposes only and does not constitute tax
        or legal advice. The VAT treatment of a transaction depends on its specific facts and
        supporting documentation. Businesses should obtain professional advice before applying the
        Reverse Charge Mechanism.
      </Callout>
    </ArticleShell>
  );
};

export default UAEVATReverseChargeMetalScrapGuide;
